import React, { useState, useEffect } from "react";
import Footer from "./Footer";
import { CircleX } from "lucide-react";
import SafeHTML from "../../../components/SafeHTML";
import { usePassportService } from "../usePassportService";
import useStickyButton from "../hooks/useStickyButton";

const PassportReject = ({ validationID, onRetry }) => {
  const { getPassportRejectReason } = usePassportService();
  const [title, setTitle] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [failReasonAndSolutionList, setFailReasonAndSolutionList] = useState([]);
  const [content, setContent] = useState([]);
  const { buttonContainerRef, sentinelRef, isSticky, buttonHeight } = useStickyButton();

  useEffect(() => {
    if (validationID) {
      getPassportRejectReason({
        validationID,
      }).then((res) => {
        if (res.status === "success") {
          setTitle(res.data.title || null);
          setErrorMessage(res.data.errorMessage);
          setFailReasonAndSolutionList(res.data.failReasonAndSolutionList || []);
          setContent(res.data.content || []);
        } else {
          setErrorMessage("There was an error while verifying your Passport.");
        }
      });
    }
  }, [validationID]);

  return (
    <div className="bg-white h-full flex flex-col justify-start p-4 min-h-screen">
      <div className="bg-gray-100 p-8 rounded-2xl mt-[calc(100vh/16)] mb-8">
        <div className="flex flex-col items-center mb-8">
          <CircleX height="72" width="72" color="red" />
        </div>
        <div className="flex flex-col">
          {title ? (
            <SafeHTML html={title} />
          ) : (
            <p className="text-2xl font-medium">
              Oops! We couldn't verify your <span className="text-blue-700">Passport</span>.
            </p>
          )}
          <p className="text-gray-500 text-md mt-4">{errorMessage}</p>

          {content.length > 0 ? (
            <div className="mt-4 text-gray-500">
              {content.map((block, index) => (
                <SafeHTML key={index} html={block} style={{ marginBottom: "0.5rem" }} />
              ))}
            </div>
          ) : (
            <ul className="text-gray-500 text-md mt-4">
              {failReasonAndSolutionList.map((item, index) => (
                <li key={index} className="mt-2">
                  <p className="text-gray-800 text-md font-medium">{item.Reason}</p>
                  <p className="text-gray-500 text-md">{item.Resolution}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="flex flex-1" />

      {/* Sentinel element for Intersection Observer */}
      <div ref={sentinelRef} style={{ height: "1px", visibility: "hidden" }} />

      {/* Spacer to maintain space when button is sticky */}
      {isSticky && buttonHeight > 0 && (
        <div style={{ height: `${buttonHeight}px`, width: "100%" }} />
      )}

      <div 
        ref={buttonContainerRef}
        style={{
          position: isSticky ? "fixed" : "relative",
          bottom: isSticky ? "0" : "auto",
          left: isSticky ? "0" : "auto",
          right: isSticky ? "0" : "auto",
          padding: isSticky ? "1rem" : "0",
          background: "transparent",
          zIndex: isSticky ? "50" : "auto",
          transition: "all 0.3s ease-in-out",
          pointerEvents: isSticky ? "none" : "auto",
        }}
      >
        <button
          className="w-2/3 bg-blue-600 text-white py-3 px-4 rounded-full flex items-center justify-center space-x-2 mx-auto transition active:scale-95 duration-150 ease-in-out"
          style={{ pointerEvents: "auto" }}
          onClick={onRetry}
          onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
          onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
          onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <span>Try Again</span>
        </button>
      </div>
      <Footer />
    </div>
  );
};

export default PassportReject;
